
import React, { useState } from 'react';
import { Formik, Form, getIn, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import InputField from 'components/Input-Field/input-field';
import SelectField from 'components/Select-Field/select-field.js';
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  FormLabel,
  IconButton,
  Stack,
  useTheme,
  ButtonGroup,
  CircularProgress,
} from '@mui/material';
import { AggregateOrderExpenses, calculateOrderTotal } from 'utils/calculations';
import { formatCurrency, formatEpochToDateTime } from 'utils/formatter';
import CTTabs from 'components/CTTabs.js';
import DataDialog from 'components/dialogs/DataDialog';
import 'rsuite/dist/rsuite.min.css';
import { DatePicker, Toggle } from 'rsuite';
import '../../../styles/rsuite-component.css';
import CustomSelectField from 'components/Select-Field/custom-select';
import { AddCircleOutlineOutlined, AddCircleOutlineRounded, HighlightOffOutlined } from '@mui/icons-material';
import BrokerForm from 'containers/Broker/broker-form';
import * as BrokerAction from 'actions/broker-action';
import * as LocationAction from 'actions/location-action';
import * as EquipmentAction from 'actions/equipment-action';
import LocationForm from 'containers/Location/location-form';
import EquipmentForm from 'containers/Equipment/equipment-form';
import { useConfirmationDialog } from 'components/dialogs/ConfirmationDialogWithHook';
import FormElements from 'components/FormElements';
import { tokens } from 'theme/tokens';
import Radar from 'radar-sdk-js';
import OrderExpense from 'containers/expenses/order-expense';
import CTAutocomplete from 'components/CTAutocomplete';
import FileUploader from 'components/FileUploader';
import { extractPreNoteDocument } from 'services/document-service';
import FTIcons from 'assets/icons/FTIcons';

const IntermodalLoadForm = (props) => {
  const {
    isEdit,
    orderForm,
    formAction,
    locations,
    brokers,
    equipments,
    saveFormHandler,
    updateFormHandler,
    cancelDialogHandler,
    saveBroker,
    saveLocation,
    saveEquipment,
  } = props;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [order, setOrder] = useState(orderForm);
  const { id, summary, origin, destination, container, chassis } = order;
  const [expense, setExpense] = useState(order.expense);
  const [expenseAction, setExpenseAction] = useState('Save');
  const [expenseList, setExpenseList] = useState(summary.expenses);
  const [isExpenseDialogOpen, setExpenseDialog] = useState(false);

  const [isPreNoteDocument, setIsPreNoteDocument] = useState(null);
  const [isPreNoteDocumentDialogOpen, setPreNoteDocumentDialog] = useState(false);
  const [isProcessingPreNoteDocument, setIsProcessingPreNoteDocument] = useState(false);
  const [preNoteToast, setPreNoteToast] = useState({});

  const [originTab, setOriginTab] = useState(origin.name.map((o, idx) => `Origin ${idx + 1}`));
  const [destinationTab, setDestinationTab] = useState(destination.name.map((o, idx) => `Destination ${idx + 1}`));

  const [currentDestinationTabIndex, setCurrentDestinationTabIndex] = useState(0);
  const [currentOriginTabIndex, setCurrentOriginTabIndex] = useState(0);

  const [isBrokerFormDialogOpen, setBrokerFormDialog] = useState(false);
  const [isLocationFormDialogOpen, setLocationFormDialog] = useState(false);
  const [isEquipmentFormDialogOpen, setEquipmentFormDialog] = useState(false);
  const [confirm, ConfirmationDialog] = useConfirmationDialog();
  const [isProcessingDistanceCalculation, setProcessingDistanceCalculation] = useState(false);
  const [originGeoList, setOriginGeoList] = useState({});
  const [destinationGeoList, setDestinationGeoList] = useState({});

  const getDisplayLabel = (item, type) => {
    let primaryText = '', secondaryText = '', geo;
    if (type === 'CONTAINER') {
      primaryText = item.line ? item.line : item.name;
      secondaryText = `Free Days: ${item.freeDays}`;
    } else if (type === 'CHASSIS') {
      primaryText = item.location ? item.line : item.name;
      secondaryText = `Rent: $${item.rent}`;      
    } else if (type === 'DRIVER') {
      /* For driver dropdown in driver pay manager page */
      primaryText = item.fullName;
      secondaryText = item.phone;
    } else if (type === 'BROKER' || type === 'LOCATION') {
      primaryText = item.name;
      secondaryText = item.address && `${item.address.street},${item.address.city},${item.address.state},${item.address.country}`;
      geo = item.address && item.address.geo;
    }
    const reactSelectObj = {
      label: <Box key={`${type}:${primaryText}`}>
        <Typography variant='body1' fontWeight={600}>{primaryText}</Typography>
        <Typography variant='caption'>{secondaryText}</Typography>
      </Box>,
      value: primaryText,
      geo: geo,
    };
    return reactSelectObj;
  };

  const addNewTab = (tabType, values) => {
    if (tabType === 'ORIGIN') {
      const { summary, origin, destination, container, chassis } = values; // This is done because of enableReinitialize flag.

      const newOrigin = {};
      newOrigin.name = [...origin.name, ''];
      newOrigin.appointment = [...origin.appointment, new Date()];
      newOrigin.openAppointment = [...origin.openAppointment, ''];
      newOrigin.puNumber = [...origin.puNumber, ''];
      newOrigin.puLocation = [...origin.puLocation, ''];
      newOrigin.notes = [...origin.notes, ''];

      const tabCount = originTab ? originTab.length + 1 : 1;
      const updatedTabs = originTab.concat([`Origin ${tabCount}`]);

      //update the Origin to have new tab and origin and rest will be same.
      order.origin = newOrigin;
      order.summary = summary;
      order.destination = destination;
      order.container = container;
      order.chassis = chassis;

      setOrder(order);
      setCurrentOriginTabIndex(previous => previous + 1);
      setOriginTab(updatedTabs);
    } else {
      const { summary, origin, destination, container, chassis } = values; // This is done because of enableReinitialize flag.

      const newDestination = {};
      newDestination.name = [...destination.name, ''];
      newDestination.appointment = [...destination.appointment, new Date()];
      newDestination.openAppointment = [...destination.openAppointment, ''];
      newDestination.referenceNumber = [...destination.referenceNumber, ''];
      newDestination.notes = [...destination.notes, ''];

      const tabCount = destinationTab ? destinationTab.length + 1 : 1;
      const updatedTabs = destinationTab.concat([`Destination ${tabCount}`]);

      //update the Origin to have new tab and origin and rest will be same.
      order.destination = newDestination;
      order.summary = summary;
      order.origin = origin;
      order.container = container;
      order.chassis = chassis;

      setOrder(order);
      setCurrentDestinationTabIndex(previous => previous + 1);
      setDestinationTab(updatedTabs);
    }
  };

  const handleRemoveTab = async (tabType, tab, value) => {
    const confirmed = await confirm({
      title: 'Remove Stop',
      icon: <HighlightOffOutlined color='error' fontSize='medium' style={{ marginRight: '10' }} />,
      content: <Typography variant='subtitle2'>Are you sure, you want to remove this stop?</Typography>
    });
    if (confirmed) {
      removeTab(tabType, tab, value);
    }
  };

  const removeAtIndex = (arr, index) => {
    return arr.filter((_, idx) => idx !== index);
  };

  const removeTab = (tabType, tab) => {
    if (tabType === 'ORIGIN') {
      const newOrigin = {};
      const { name, appointment, openAppointment, puNumber, puLocation, notes } = origin;
      const index = Number(tab.split(' ')[1]) - 1;
      newOrigin.name = removeAtIndex(name, index);
      newOrigin.appointment = removeAtIndex(appointment, index);
      newOrigin.openAppointment = removeAtIndex(openAppointment, index);
      newOrigin.puNumber = removeAtIndex(puNumber, index);
      newOrigin.puLocation = removeAtIndex(puLocation, index);
      newOrigin.notes = removeAtIndex(notes, index);

      const filteredTabs = originTab.filter((o_tab) => { return o_tab !== tab; });

      order.origin = newOrigin;
      setOrder(order);
      setCurrentOriginTabIndex(previous => previous - 1);
      setOriginTab(filteredTabs);

    } else if (tabType === 'DESTINATION') {
      const newDestination = {};
      const { name, appointment, openAppointment, referenceNumber, notes } = destination;
      const index = Number(tab.split(' ')[1]) - 1;
      newDestination.name = removeAtIndex(name, index);
      newDestination.appointment = removeAtIndex(appointment, index);
      newDestination.referenceNumber = removeAtIndex(referenceNumber, index);
      newDestination.notes = removeAtIndex(notes, index);
      newDestination.openAppointment = removeAtIndex(openAppointment, index);

      const filteredTabs = destinationTab.filter((d_tab) => {
        return d_tab !== tab;
      });

      order.destination = newDestination;
      setOrder(order);
      setCurrentDestinationTabIndex(previous => previous - 1);
      setDestinationTab(filteredTabs);
    }
  };

  const changeTab = (tabType, tabIndex) => {
    if (tabType === 'ORIGIN') {
      setCurrentOriginTabIndex(tabIndex);
    } else if (tabType === 'DESTINATION') {
      setCurrentDestinationTabIndex(tabIndex);
    }
  };

  /* Expenses */
  const editRow = (row) => {
    setExpense(row);
    setExpenseAction('Update');
  };

  const saveExpense = (expense) => {
    expense.id = expenseList.length + 1;
    setExpenseList([...expenseList, expense]);
    setExpense(Object.create(expense, order.expense));
  };

  const updateExpense = (expense) => {
    const index = expenseList.findIndex(e => e.id === expense.id);
    if (index !== -1) {
      const data = expenseList.map(item => item.id === expense.id ? { ...expense } : { ...item });
      setExpenseList(data);
      setExpense(Object.create(expense, order.expense));
    }
  };

  const deleteExpense = (expense) => {
    const index = expenseList.findIndex(e => e.id === expense.id);
    if (index !== -1) {
      const data = [...expenseList.filter((item) => item.id !== expense.id)];
      setExpenseList(data);
    }
  };

  const OrderSchema = Yup.object().shape({
    summary: Yup.object().shape({
      orderType: Yup.string().required('Order Type is required'),
      broker: Yup.string().required('Broker is required.'),
      brokerOrderNumber: Yup.string().required('Broker Order Number is required'),
      orderRate: Yup.number()
        .required('Order Rate is required')
        .min(0, 'Order Rate cannot be negative'),
      fuelCharges: Yup.number()
        .required('Fuel charges is required')
        .min(0, 'Fuel Charges cannot be negative'),
    }),
    origin: Yup.object().shape({
      name: Yup.string().required('Origin Name is required.'),
      appointment: Yup.string().required('Origin appointment is required'),
    }),
    destination: Yup.object().shape({
      name: Yup.string().required('Destination Name is required.'),
      appointment: Yup.string().required('Destination appointment is required')
    }),
  });

  /* If Edit Order */
  if (isEdit) {
    summary.broker = getDisplayLabel(summary.broker, 'BROKER');
    origin.name = origin.name.map(item => getDisplayLabel(item, 'LOCATION'));
    destination.name = destination.name.map(item => getDisplayLabel(item, 'LOCATION'));
    if (container.line) {
      container.line = getDisplayLabel(container.line, 'CONTAINER');
    }
    if (chassis.location) {
      chassis.location = getDisplayLabel(chassis.location, 'CHASSIS');
    }
    if (chassis.pickupChassisLocation) {
      chassis.pickupChassisLocation = getDisplayLabel(chassis.location, 'CHASSIS');
    }
  }

  const closeFormDialog = (dialog) => {
    if (dialog === 'broker') {
      setBrokerFormDialog(false);
    } if (dialog === 'location') {
      setLocationFormDialog(false);
    } else {
      setEquipmentFormDialog(false);
    }
    props.resetToastState(dialog);
  }

  const handleLocationChange = (setFieldValue, locationType, location, tabIndex) => {
    const radarLocation = location.geo && location.geo.lat && location.geo.lng ? new Object({
      latitude: location.geo.lat,
      longitude: location.geo.lng
    }) : null;

    if (radarLocation) {
      let originGeoByIndex = { ...originGeoList };
      let destinationGeoByIndex = { ...destinationGeoList };
      if (locationType === 'ORIGIN') {
        originGeoByIndex = { [tabIndex]: radarLocation };
        setOriginGeoList(originGeoByIndex);
      } else {
        destinationGeoByIndex = { [tabIndex]: radarLocation };
        setDestinationGeoList(destinationGeoByIndex);
      }
      //Make API call only when both origin and destinations are selected.
      if (Object.keys(originGeoByIndex).length > 0 && Object.keys(destinationGeoByIndex).length > 0) {
        setProcessingDistanceCalculation(true);
        Radar.matrix({
          origins: Object.values(originGeoByIndex),
          destinations: Object.values(destinationGeoByIndex),
          modes: 'truck',
          units: 'imperial'
        }).then(result => {
          result.matrix.forEach(matrixResult => {
            matrixResult.forEach(mr => {
              const { distance, duration } = mr;
              if (distance && distance.text) {
                const arr = distance.text.split(' ');
                if (arr && arr.length === 2) {
                  setFieldValue('summary.distance', Number(arr[0]));
                  setFieldValue('summary.distanceUnit', arr[1]);
                }
                setFieldValue('summary.duration', duration.text);
              }
            });
          });
          setProcessingDistanceCalculation(false);
        });
      }
    }
  };

  const handleCreateLoadWithAI = (PreNote) => {
    setIsProcessingPreNoteDocument(true);
    setPreNoteToast({ type: 'INFO', message: 'Loading Document...' });
    setTimeout(() => {
      setPreNoteToast({ type: 'INFO', message: 'Extracting Load Information...' });
      extractPreNoteDocument(PreNote).then((res) => {
        if (res.status === 200 && res.statusText === 'OK') {
          const data = res.data;
          const preNoteLoad = {};
          // const PreNoteLoad = { summary: data.summary, origin: data.originLocationsLocations, destination: data.destinationLocationsLocations, equipment: data.equipment };
          preNoteLoad.summary = {
            broker: data.summary.broker,
            brokerOrderNumber: data.summary.brokerOrderNumber,
            orderType: 'DELIVERY',
            orderRate: data.summary.lineHaulCharges,
            fuelCharges: data.summary.fuelCharges,
            fuelChargesType: data.summary.fuelChargesType,
            appointment: 'YES', //data.summary.appointment,
            palletQty: data.summary.palletQty,
            notes: data.summary.notes,
          };
          preNoteLoad.origin = {
            name: data.originLocations.map(t => t),
            appointment: data.originLocations.map(t => formatEpochToDateTime(t.appointment)),
            isOpenTime: data.originLocations.map(t => t.openTime || [false]),
            openAppointment: data.originLocations.map(t => t.openAppointment),
            puNumber: data.originLocations.map(t => t.puNumber),
            puLocation: data.originLocations.map(t => t.puLocation),
            notes: data.originLocations.map(t => t.notes),
          };
          preNoteLoad.destination = {
            name: data.destinationLocations.map(t => t),
            appointment: data.destinationLocations.map(t => formatEpochToDateTime(t.appointment)),
            isOpenTime: data.destinationLocations.map(t => t.openTime || [false]),
            openAppointment: data.destinationLocations.map(t => t.openAppointment),
            referenceNumber: data.destinationLocations.map(t => t.referenceNumber),
            notes: data.destinationLocations.map(t => t.notes),
          };
          preNoteLoad.container = {

          };
          preNoteLoad.chassis = {

          };
          // preNoteLoad.container = {
          //   line: container,
          //   deliveryContainerNumber: container.deliveryContainerNumber,
          //   pickupContainerNumber: container.pickupContainerNumber,
          //   notes: container.notes,
          //   lastFreeDate: container.lastFreeDate === '0' ? null : container.lastFreeDate,
          //   bookingNumber: container.bookingNumber,
          //   isCustomDropLocation: container.isCustomDropLocation,
          //   dropLocation: container.dropLocation,
          // };        
          setOrder(preNoteLoad);
          setIsPreNoteDocument(true);
          setPreNoteToast({ type: 'SUCCESS', message: 'Load information extracted. Please verify and save the load.' });
        }
      })
        .catch((err) => {
          setPreNoteToast({ type: 'ERROR', message: 'Failed to extract load information from document. Please try again.' });
        })
        .finally(() => {
          setIsProcessingPreNoteDocument(false);
        });
    }, 1000);
  };

  const { isProcessing, toast } = props; //Toast
  return (
    <Box>
      <Formik
        initialValues={{ summary, origin, destination, container, chassis }}
        enableReinitialize={true}
        validationSchema={OrderSchema}
        onSubmit={(values) => {
          const dispatchOrder = Object.create(null);
          //Check if expenses are added/updated/deleted when creating or updating order.
          values.summary.expenses = expenseList;
          dispatchOrder.summary = values.summary;
          dispatchOrder.summary.orderCreateDate = new Date();
          dispatchOrder.origin = values.origin;
          dispatchOrder.destination = values.destination;
          dispatchOrder.container = values.container;
          dispatchOrder.chassis = values.chassis;
          if (formAction === 'Save') {
            saveFormHandler(dispatchOrder);
          } else {
            dispatchOrder.id = id;
            updateFormHandler(dispatchOrder);
          }
        }}
      >
        {({
          errors,
          touched,
          values,
          handleBlur,
          handleChange,
          setFieldValue,
        }) => (
          <Form>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={'stretch'}
            >
              <FormElements.FormSection sx={{ flex: 1, width: '100%', flexShrink: 0 }}>
                {/* Load Summary */}
                <FormElements.FormSubsection>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12 }}>
                      <Box
                        display={'flex'}
                        justifyContent={'space-between'}
                      >
                        <Box display={'flex'} gap={2} alignItems={'center'}>
                          <Typography variant='subtitle2' style={{ fontWeight: 500 }}>Do you have an appointment?</Typography>
                          <ButtonGroup size='small' color='secondary'>
                            <Button
                              variant={values.summary.appointment === true ? 'contained' : 'outlined'}
                              onClick={() => {setFieldValue('summary.appointment', true)}}
                            >
                              YES
                            </Button>
                            <Button
                              variant={values.summary.appointment === false ? 'contained' : 'outlined'}
                              onClick={() => {setFieldValue('summary.appointment', false)}}
                            >
                              NO
                            </Button>
                          </ButtonGroup>
                        </Box>
                        <Button
                          size='small'
                          color='secondary'
                          variant='outlined'
                          startIcon={<FTIcons.AIIcon width={20} height={20} fill={colors.secondary.main}/>}
                          onClick={() => setPreNoteDocumentDialog(true)}
                        >Create Load With AI
                        </Button>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6 }}>
                      <SelectField
                        fullWidth={true}
                        name='summary.orderType'
                        label={'Select Order Type'}
                        variant='outlined'
                        value={values.summary.orderType}
                        error={Boolean(
                          getIn(touched, 'summary.orderType') &&
                          getIn(errors, 'summary.orderType')
                        )}
                        helperText={Boolean(
                          getIn(touched, 'summary.orderType') &&
                          getIn(errors, 'summary.orderType')
                        )}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        options={['DELIVERY', 'PICKUP', 'DROP', 'DROP_PICK']}
                        size='small'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box display='flex'>
                        <Box flex={1}>                      
                          <CustomSelectField
                            type='BROKER'
                            id='brokers'
                            label='Broker Name'
                            name='summary.broker'
                            data={brokers && brokers.map(b => getDisplayLabel(b, 'BROKER'))}
                            value={values.summary.broker}
                            onChange={selected => setFieldValue('summary.broker', selected)}
                          />
                          <ErrorMessage name={`summary.broker`} component={'grid'} style={{ color: colors.error.main }} />
                        </Box>
                        <span style={{ color: 'red', fontSize: 'medium' }}>*</span>                        
                        <IconButton color='primary' onClick={() => setBrokerFormDialog(true)} size='small'><AddCircleOutlineOutlined /></IconButton>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InputField
                        type='text'
                        label='Broker Order#'
                        name='summary.brokerOrderNumber'
                        value={values.summary.brokerOrderNumber}
                        variant='outlined'
                        fullWidth
                        error={Boolean(
                          getIn(touched, 'summary.brokerOrderNumber') &&
                          getIn(errors, 'summary.brokerOrderNumber')
                        )}
                        helperText={Boolean(
                          getIn(touched, 'summary.brokerOrderNumber') &&
                          getIn(errors, 'summary.brokerOrderNumber')
                        )}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                    </Grid>
                    <Grid size={{ xs: 4, sm: 3 }}>
                      <InputField
                        type='number'
                        label='Order Rate'
                        name='summary.orderRate'
                        value={values.summary.orderRate}
                        variant='outlined'
                        fullWidth
                        error={Boolean(
                          getIn(touched, 'summary.orderRate') &&
                          getIn(errors, 'summary.orderRate')
                        )}
                        helperText={Boolean(
                          getIn(touched, 'summary.orderRate') &&
                          getIn(errors, 'summary.orderRate')
                        )}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                    </Grid>
                    <Grid size={{ xs: 4, sm: 3 }}>
                      <InputField
                        type='number'
                        label='FSC'
                        name='summary.fuelCharges'
                        value={values.summary.fuelCharges}
                        variant='outlined'
                        fullWidth
                        error={Boolean(
                          getIn(touched, 'summary.fuelCharges') &&
                          getIn(errors, 'summary.fuelCharges')
                        )}
                        helperText={Boolean(
                          getIn(touched, 'summary.fuelCharges') &&
                          getIn(errors, 'summary.fuelCharges')
                        )}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                      <Toggle
                        name='summary.fuelChargesType'
                        value={values.summary.fuelChargesType}
                        checkedChildren={'$'}
                        unCheckedChildren={'%'}
                        defaultChecked={values.summary.fuelChargesType === '$'}
                        onChange={checked => {
                          const val = checked ? '$' : '%';
                          setFieldValue('summary.fuelChargesType', val);
                        }
                        }
                        style={{ position: 'absolute', margin: '8px -50px', color: 'red' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 4, sm: 2 }}>
                      <Box display={'flex'} alignItems={'center'} mt={1}>
                        <IconButton color={'primary'} onClick={() => setExpenseDialog(true)} size='small'>
                          <AddCircleOutlineOutlined />
                        </IconButton>
                        <Typography variant='body1'>Expense</Typography>
                      </Box>
                    </Grid>                    
                  </Grid>
                </FormElements.FormSubsection>

                {/* Load Origin */}
                <FormElements.FormSubsection>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12 }}>
                      <CTTabs
                        labels={originTab}
                        tabIndex={currentOriginTabIndex}
                        addNewButtonEnabled={true}
                        removeButtonEnabled={true}
                        fnOnAddTabHandler={() => addNewTab('ORIGIN', values)}
                        fnOnRemoveTabHandler={(tab) => handleRemoveTab('ORIGIN', tab, values)}
                        fnOnTabChangeHandler={tabIndex => changeTab('ORIGIN', tabIndex)}
                      />
                      {ConfirmationDialog}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <Box display='flex'>
                        <div style={{ flex: 1 }}>
                          <CustomSelectField
                            type='LOCATION'
                            id='origins'
                            label='Origin Name'
                            name={`origin.name[${currentOriginTabIndex}]`}
                            data={locations.map(c => getDisplayLabel(c, 'LOCATION'))}
                            value={values.origin.name[currentOriginTabIndex]}
                            onChange={selected => {
                              setFieldValue(`origin.name[${currentOriginTabIndex}]`, selected);                              
                              handleLocationChange(setFieldValue, 'ORIGIN', selected, currentOriginTabIndex);
                            }}
                          />
                          <ErrorMessage name={`origin.name`} component={'grid'} style={{ color: colors.error.main }} />
                        </div>
                        <span style={{ color: 'red', fontSize: 'medium' }}>*</span>
                        <IconButton sx={{ height: '35px' }} color='primary' onClick={() => setLocationFormDialog(true)} size='small'><AddCircleOutlineOutlined /></IconButton>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <DatePicker
                        format='MM/dd/yyyy hh:mm aa'
                        showMeridiem
                        showWeekNumbers
                        name={`origin.appointment[${currentOriginTabIndex}]`}
                        value={values.origin.appointment[currentOriginTabIndex]}
                        error={Boolean(
                          getIn(touched, 'origin.appointment') &&
                          getIn(errors, 'origin.appointment')
                        )}
                        helperText={Boolean(
                          getIn(touched, 'origin.appointment') &&
                          getIn(errors, 'origin.appointment')
                        )}
                        onChange={(date) => setFieldValue(`origin.appointment[${currentOriginTabIndex}]`, date)}
                        required={true}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InputField
                        type='text'
                        label='Open Appointment'
                        name={`origin.openAppointment[${currentOriginTabIndex}]`}
                        value={values.origin.openAppointment[currentOriginTabIndex]}
                        variant='outlined'
                        fullWidth
                        error={Boolean(touched.openAppointment && errors.openAppointment)}
                        helperText={touched.openAppointment && errors.openAppointment}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InputField
                        type='text'
                        label='Enter PU-Number'
                        name={`origin.puNumber[${currentOriginTabIndex}]`}
                        value={values.origin.puNumber[currentOriginTabIndex]}
                        variant='outlined'
                        fullWidth
                        error={Boolean(touched.puNumber && errors.puNumber)}
                        helperText={touched.puNumber && errors.puNumber}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InputField
                        type='text'
                        label='Enter PU-Location'
                        name={`origin.puLocation[${currentOriginTabIndex}]`}
                        value={values.origin.puLocation[currentOriginTabIndex]}
                        variant='outlined'
                        fullWidth
                        error={Boolean(touched.puLocation && errors.puLocation)}
                        helperText={touched.puLocation && errors.puLocation}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <InputField
                        label='Instructions'
                        type='text'
                        name={`origin.notes[${currentOriginTabIndex}]`}
                        value={values.origin.notes[currentOriginTabIndex]}
                        variant='outlined'
                        fullWidth
                        error={Boolean(touched.notes && errors.notes)}
                        helperText={touched.notes && errors.notes}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                    </Grid>
                  </Grid>
                </FormElements.FormSubsection>

                {/* Destination */}
                <FormElements.FormSubsection>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12 }}>
                      <CTTabs
                        labels={destinationTab}
                        tabIndex={currentDestinationTabIndex}
                        addNewButtonEnabled={true}
                        removeButtonEnabled={true}
                        fnOnAddTabHandler={() => addNewTab('DESTINATION', values)}
                        fnOnRemoveTabHandler={(tab) => handleRemoveTab('DESTINATION', tab, values)}
                        fnOnTabChangeHandler={(tabIndex) => changeTab('DESTINATION', tabIndex)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <Box display='flex'>
                        <div style={{ flex: 1 }}>
                          <CustomSelectField
                            type='LOCATION'
                            id='destinations'
                            label='Destination Name'
                            name={`destination.name[${currentDestinationTabIndex}]`}
                            data={locations.map(c => getDisplayLabel(c, 'LOCATION'))}
                            value={values.destination.name[currentDestinationTabIndex]}
                            onChange={selected => {
                              setFieldValue(`destination.name[${currentDestinationTabIndex}]`, selected);
                              handleLocationChange(setFieldValue, 'DESTINATION', selected, currentDestinationTabIndex);                              
                            }}                              
                          />
                          <ErrorMessage name={`destination.name`} component={'grid'} style={{ color: colors.error.main }} />
                        </div>
                        <span style={{ color: 'red', fontSize: 'medium' }}>*</span>
                        <IconButton
                          color='primary'
                          sx={{ height: '35px' }}
                          onClick={() => setLocationFormDialog(true)}
                          size="large"><AddCircleOutlineRounded /></IconButton>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <DatePicker
                        format='MM/dd/yyyy hh:mm aa'
                        showMeridiem
                        showWeekNumbers
                        name={`destination.appointment[${currentDestinationTabIndex}]`}
                        value={values.destination.appointment[currentDestinationTabIndex]}
                        error={Boolean(
                          getIn(touched, 'destination.appointment') &&
                          getIn(errors, 'destination.appointment')
                        )}
                        helperText={Boolean(
                          getIn(touched, 'destination.appointment') &&
                          getIn(errors, 'destination.appointment')
                        )}
                        onChange={(date) => setFieldValue(`destination.appointment[${currentDestinationTabIndex}]`, date)}
                        required={true}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <InputField
                        type='text'
                        label='P/O or Del Number'
                        name={`destination.referenceNumber[${currentDestinationTabIndex}]`}
                        value={values.destination.referenceNumber[currentDestinationTabIndex]}
                        variant='outlined'
                        fullWidth
                        error={Boolean(touched.referenceNumber && errors.referenceNumber)}
                        helperText={touched.referenceNumber && errors.referenceNumber}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <InputField
                        type='text'
                        label='Open Appointment'
                        name={`destination.openAppointment[${currentDestinationTabIndex}]`}
                        value={values.destination.openAppointment[currentDestinationTabIndex]}
                        variant='outlined'
                        fullWidth
                        error={Boolean(touched.openAppointment && errors.openAppointment)}
                        helperText={touched.openAppointment && errors.openAppointment}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InputField
                        label='Instructions'
                        type='text'
                        name={`destination.notes[${currentDestinationTabIndex}]`}
                        value={values.destination.notes[currentDestinationTabIndex]}
                        variant='outlined'
                        fullWidth
                        error={Boolean(touched.notes && errors.notes)}
                        helperText={touched.notes && errors.notes}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                    </Grid>
                  </Grid>
                </FormElements.FormSubsection>

                {/* <IntermodalEquipment /> */}
                <FormElements.FormSubsection>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <CustomSelectField
                        type='CONTAINER'
                        id='containers'
                        label='Container Line'
                        name='container.line'
                        data={equipments.filter(e => e.type === 'CONTAINER').map(b => getDisplayLabel(b, 'CONTAINER'))}
                        value={values.container.line}
                        onChange={selected => setFieldValue('container.line', selected)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InputField
                        type='text'
                        label='Delivery Container Number'
                        name='container.deliveryContainerNumber'
                        value={values.container.deliveryContainerNumber}
                        variant='outlined'
                        fullWidth
                        error={Boolean(
                          getIn(touched, 'container.deliveryContainerNumber') &&
                          getIn(errors, 'container.deliveryContainerNumber')
                        )}
                        helperText={Boolean(
                          getIn(touched, 'container.deliveryContainerNumber') &&
                          getIn(errors, 'container.deliveryContainerNumber')
                        )}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                        disabled={summary.orderType === 'PICKUP'}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        id='date'
                        label='Last Free Day'
                        type='date'
                        name='container.lastFreeDate'
                        defaultValue={values.container.lastFreeDate}
                        onChange={handleChange}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth={true}
                        size='small'
                        variant='outlined'
                        error={Boolean(touched.lastFreeDate && errors.lastFreeDate)}
                        helperText={touched.lastFreeDate && errors.lastFreeDate}
                        style={{
                          marginTop: '0px'
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InputField
                        type='text'
                        label='Pickup Container Number'
                        name='container.pickupContainerNumber'
                        value={values.container.pickupContainerNumber}
                        variant='outlined'
                        fullWidth
                        error={Boolean(
                          getIn(touched, 'container.pickupContainerNumber') &&
                          getIn(errors, 'container.pickupContainerNumber')
                        )}
                        helperText={Boolean(
                          getIn(touched, 'container.pickupContainerNumber') &&
                          getIn(errors, 'container.pickupContainerNumber')
                        )}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                        disabled={summary.orderType === 'DELIVERY'}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InputField
                        type='text'
                        label='Booking Number'
                        name='container.bookingNumber'
                        onChange={handleChange}
                        value={values.container.bookingNumber}
                        fullWidth={true}
                        required={false}
                        disabled={summary.orderType !== 'PICKUP'}
                        size='small'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <InputField
                        type='text'
                        name='container.notes'
                        label='Container Notes'
                        value={values.container.notes}
                        fullWidth={true}
                        required={false}
                        error={Boolean(touched.notes && errors.notes)}
                        helperText={touched.notes && errors.notes}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                    </Grid>
                    <Grid size={{ xs: 4, sm: 4 }}>
                      <Box display={'flex'} alignItems={'center'}>
                        <FormLabel component='legend'>Is Drop Location</FormLabel>
                        <Toggle
                          name='container.isCustomDropLocation'
                          value={values.container.isCustomDropLocation}
                          checkedChildren='YES'
                          unCheckedChildren='NO'
                          defaultChecked={values.container.isCustomDropLocation}
                          onChange={(val) => setFieldValue('container.isCustomDropLocation', val)}
                          fullWidth={true}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 8, sm: 8 }}>
                      <CTAutocomplete
                        margin={'none'}
                        disabled={!values.container.isCustomDropLocation}
                        fnHandleSelectedLocation={loc => setFieldValue('container.dropLocation', loc.formattedAddress)}
                      />
                    </Grid>
                  </Grid>
                </FormElements.FormSubsection>

                <FormElements.FormSubsection>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <CustomSelectField
                        type='CHASSIS'
                        id='chassis'
                        label='Chassis Company'
                        name='chassis.company'
                        data={equipments.filter(e => e.type === 'CHASSIS').map(b => getDisplayLabel(b, 'CHASSIS'))}
                        value={values.chassis.company}
                        onChange={selected => setFieldValue('chassis.company', selected)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <InputField
                        type='text'
                        label='Chassis Number'
                        name='chassis.chassisNumber'
                        value={values.chassis.chassisNumber}
                        variant='outlined'
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size='small'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <FormLabel component='legend' style={{ margin: '-5px 0px 5px' }}>Chassis Split</FormLabel>
                      <Toggle
                        name='chassis.isChassisSplit'
                        value={values.chassis.isChassisSplit}
                        checkedChildren='YES'
                        unCheckedChildren='NO'
                        defaultChecked={values.chassis.isChassisSplit}
                        onChange={(val) => setFieldValue('chassis.isChassisSplit', val)}
                        fullWidth={true}
                      />
                    </Grid>
                    {summary.orderType === 'DROP_PICK' ? <React.Fragment>
                      <Grid size={{ xs: 12, sm: 5 }}>
                        <CustomSelectField
                          type='CHASSIS'
                          id='chassis'
                          label='Pick up Chassis Company'
                          name='chassis.pickupChassisCompany'
                          data={equipments.filter(e => e.type === 'CHASSIS').map(b => getDisplayLabel(b, 'CHASSIS'))}
                          value={values.chassis.pickupChassisCompany}
                          onChange={selected => setFieldValue('chassis.pickupChassisCompany', selected)}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <InputField
                          type='text'
                          label='Pickup Chassis Number'
                          name='chassis.pickupChassisNumber'
                          value={values.chassis.pickupChassisNumber}
                          variant='outlined'
                          fullWidth
                          onBlur={handleBlur}
                          onChange={handleChange}
                          size='small'
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <FormLabel component='legend' style={{ margin: '-5px 0px 5px' }}>Pickup Chassis Split</FormLabel>
                        <Toggle
                          name='chassis.isPickupChassisSplit'
                          value={values.chassis.isPickupChassisSplit}
                          checkedChildren='YES'
                          unCheckedChildren='NO'
                          defaultChecked={values.chassis.isPickupChassisSplit}
                          onChange={(val) => setFieldValue('chassis.isPickupChassisSplit', val)}
                          fullWidth={true}
                        />
                      </Grid>
                    </React.Fragment> : null
                    }
                  </Grid>
                </FormElements.FormSubsection>
              </FormElements.FormSection>
              <Stack spacing={1} sx={{ width: { xs: '100%', md: '280px' }, flexShrink: 0 }}>
                <FormElements.SideBar height={'210px'}>
                  <Typography variant='body1' color={colors.neutral[700]}>Order Notes</Typography>
                  <InputField
                    type='text'
                    placeholder='Add additional'
                    name='summary.notes'
                    value={values.summary.notes}
                    variant='outlined'
                    fullWidth
                    error={Boolean(touched.notes && errors.notes)}
                    helperText={touched.notes && errors.notes}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    size='small'
                    sx={{
                      '&.MuiTextField-root': {
                        height: '150px',
                      },
                      '.MuiInputBase-root': {
                        height: 'inherit',
                      }
                    }}
                  />
                </FormElements.SideBar>
                <FormElements.SideBar height={'280px'} sx={{ flexShrink: 0 }}>
                  {isProcessingDistanceCalculation && <Box textAlign={'center'}> <CircularProgress /> </Box>}                  
                  <Typography variant='subtitle1' fontWeight={600} color={colors.neutral[900]}>{'Order Summary'}</Typography>
                  <Stack spacing={1} alignItems={'stretch'}>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <Typography variant='body1' color={colors.neutral[500]}>Order Rate</Typography>
                      <Typography variant='body1' fontWeight={500} color={colors.neutral[500]}>{formatCurrency(values.summary.orderRate)}</Typography>
                    </Box>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <Typography variant='body1' color={colors.neutral[500]}>FSC</Typography>
                      <Typography variant='body1' fontWeight={500} color={colors.neutral[500]}>{formatCurrency(values.summary.fuelCharges)}</Typography>
                    </Box>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <Typography variant='body1' color={colors.neutral[500]}>Expenses</Typography>
                      <Typography variant='body1' fontWeight={500} color={colors.neutral[500]}>{formatCurrency(AggregateOrderExpenses(expenseList))}</Typography>
                    </Box>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <Typography variant='subtitle2' color={colors.neutral[800]}>Total</Typography>
                      <Typography variant='subtitle2' fontWeight={500} color={colors.neutral[800]}>{formatCurrency(calculateOrderTotal(values.summary.orderRate, values.summary.fuelCharges, values.summary.fuelChargesType, expenseList))}</Typography>
                    </Box>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <Typography variant='body1' color={colors.neutral[500]}>Total Miles</Typography>
                      <Typography variant='body1' fontWeight={500} color={colors.neutral[500]}>{values.summary.distance || '-'}</Typography>
                    </Box>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <Typography variant='body1' color={colors.neutral[500]}>Duration</Typography>
                      <Typography variant='body1' fontWeight={500} color={colors.neutral[500]}>{values.summary.duration || '-'}</Typography>
                    </Box>
                  </Stack>
                </FormElements.SideBar>
                {/* <FormElements.SideBar height={'295px'}>
                  <Stack>
                    <Box>
                      <Avatar sx={{ background: 'none' }}>{<FTIcons.Folder />}</Avatar>
                      <Button
                        size='large'
                        color='primary'
                        variant='outlined'
                        startIcon={<FTIcons.UploadSimple width={24} height={24} />}
                      >Upload Document</Button>
                      <Typography sx={{ fontSize: 9, fontWeight: 400 }} color={colors.neutral[400]}>
                        Supported format: PDF, Docs. Max file size: 5MB.
                      </Typography>
                    </Box>
                    <Box>
                      <List>
                        <ListItemIcon>{FTIcons.Folder}</ListItemIcon>
                        <Box>
                          <ListItemText primaryText='Proof of Delivery'></ListItemText>
                          <ListItemIcon secondaryText='Final proof of delivery'></ListItemIcon>
                        </Box>
                        <IconButton>
                          <DeleteOutline />
                        </IconButton>
                      </List>
                    </Box>

                  </Stack>
                </FormElements.SideBar> */}
              </Stack>
            </Stack>
            <Box display='flex' justifyContent='flex-end' p={2}>
              <Button onClick={() => cancelDialogHandler()}>Cancel</Button>
              <Button
                type='submit'
                variant='contained'
                color='primary'
              >
                {formAction}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
      {isBrokerFormDialogOpen && <DataDialog
        toast={toast}
        isProcessing={isProcessing}
        open={isBrokerFormDialogOpen}
        title={'Add New Broker'}
        onCloseHandler={() => closeFormDialog('broker')}
      >
        <BrokerForm
          action='Save'
          broker={{
            name: '',
            email: '',
            phone: '',
            street: '',
            city: '',
            state: '',
            zipcode: '',
            country: '',
            isActive: true,
          }}
          isFormModal={true}
          saveFormHandler={(model) => saveBroker(model)}
        />
      </DataDialog>}
      {isEquipmentFormDialogOpen && <DataDialog
        toast={toast}
        isProcessing={isProcessing}
        open={isEquipmentFormDialogOpen}
        title={'Add New Equipment'}
        onCloseHandler={() => closeFormDialog('equipment')}
      >
        <EquipmentForm
          action='Save'
          equipment={{
            name: '',
            type: '',
            description: '',
            height: '',
            length: '',
            street: '',
            city: '',
            state: '',
            zipcode: '',
            country: '',
            freeDays: 0,
            rent: 0,
            isActive: true,
          }}
          isFormModal={true}
          saveFormHandler={model => saveEquipment(model)}
        />
      </DataDialog>}
      {isLocationFormDialogOpen && <DataDialog
        toast={toast}
        isProcessing={isProcessing}
        open={isLocationFormDialogOpen}
        title={'Add New Location'}
        onCloseHandler={() => closeFormDialog('location')}
      >
        <LocationForm
          action='Save'
          location={{
            name: '',
            email: '',
            phone: '',
            street: '',
            city: '',
            state: '',
            zipcode: '',
            country: '',
            isActive: true,
          }}
          isFormModal={true}
          saveFormHandler={model => saveLocation(model)}
        />
      </DataDialog>}
      {isExpenseDialogOpen && <DataDialog
        open={isExpenseDialogOpen}
        title={'Expense'}
        width={'md'}
        onCloseHandler={() => setExpenseDialog(false)}
      >
        <OrderExpense
          action={expenseAction}
          expense={expense}
          expenseList={expenseList}
          fnOnSaveHandler={(expense) => saveExpense(expense)}
          fnOnUpdateHandler={(expense) => updateExpense(expense)}
          fnOnEditHandler={(expense) => editRow(expense)}
          fnOnDeleteHandler={(expense) => deleteExpense(expense)}
        />
      </DataDialog>}
      {isPreNoteDocumentDialogOpen && <DataDialog
        open={isPreNoteDocumentDialogOpen}
        title={'Pre-Load Document'}
        width={'md'}
        onCloseHandler={() => { setPreNoteDocumentDialog(false); setPreNoteToast(null); }}
      >
        <FileUploader
          name={'LoadForm'}
          isProcessing={isProcessingPreNoteDocument}
          message={preNoteToast}
          fnOnUploadHandler={(file) => handleCreateLoadWithAI(file)}
        />
      </DataDialog>}      
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    saveBroker: (data) => dispatch(BrokerAction.save(data)),
    saveLocation: (data) => dispatch(LocationAction.save(data)),
    saveEquipment: (data) => dispatch(EquipmentAction.save(data)),
    resetToastState: (entity) => {
      if (entity === 'broker') {
        dispatch(BrokerAction.resetState('RESET_TOAST_STATE'));
      } else if (entity === 'location') {
        dispatch(LocationAction.resetState('RESET_TOAST_STATE'));
      } else {
        dispatch(EquipmentAction.resetState('RESET_TOAST_STATE'));
      }
    }
  }
};

function mapStatesToProps(state) {
  // Whatevet is returned will show up as props
  const { brokers } = state.brokerReducer;
  const { locations } = state.locationReducer;
  const { equipments } = state.equipmentReducer;
  let toast = {}, isProcessing = false;
  if (state.brokerReducer.toast && state.brokerReducer.toast.type && state.brokerReducer.toast.message) {
    toast = state.brokerReducer.toast;
    isProcessing = state.brokerReducer.isProcessing;
  } else if (state.locationReducer.toast && state.locationReducer.toast.type && state.locationReducer.toast.message) {
    toast = state.locationReducer.toast;
    isProcessing = state.locationReducer.isProcessing;
  } else {
    toast = state.equipmentReducer.toast;
    isProcessing = state.equipmentReducer.isProcessing;
  }

  return {
    brokers, locations, equipments, toast, isProcessing
  };
};

export default connect(mapStatesToProps, mapDispatchToProps)(IntermodalLoadForm);